import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, BarChart as ReBarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, LabelList,
  PieChart, Pie, Label,
} from 'recharts';
import { useAuthFetch } from '@/hooks';
import type { DoctorStatsResponse } from '@dermalink/shared';
import styles from './Statistics.module.css';

type Period = '30d' | '3m' | '6m' | 'all';

const PERIODS: { key: Period; label: string }[] = [
  { key: '30d', label: '30 dagen' },
  { key: '3m',  label: '3 maanden' },
  { key: '6m',  label: '6 maanden' },
  { key: 'all', label: 'Alles' },
];

const NL_MONTHS = ['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'];

function monthLabel(yyyyMM: string): string {
  const m = parseInt(yyyyMM.slice(5, 7), 10) - 1;
  return NL_MONTHS[m] ?? yyyyMM;
}

function pad2(n: number) { return String(n).padStart(2, '0'); }

function getTodayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function getMondayStr(): string {
  const d = new Date();
  const diff = d.getDay() === 0 ? -6 : 1 - d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() + diff);
  return `${mon.getFullYear()}-${pad2(mon.getMonth() + 1)}-${pad2(mon.getDate())}`;
}

function isCurrentBucket(bucket: string, period: Period): boolean {
  if (period === '30d') return bucket === getTodayStr();
  if (period === '3m')  return bucket === getMondayStr();
  const now = new Date();
  return bucket === `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
}

function bucketLabel(bucket: string, period: Period): string {
  if (period === '30d' || period === '3m') {
    const d = new Date(bucket + 'T00:00:00');
    return `${d.getDate()} ${NL_MONTHS[d.getMonth()]}`;
  }
  return monthLabel(bucket);
}

function starsDisplay(rating: number): string {
  const full  = Math.floor(rating);
  const half  = rating - full >= 0.25 ? 1 : 0;
  const empty = 5 - full - half;
  return '★'.repeat(full) + (half ? '½' : '') + '☆'.repeat(empty);
}

function formatRelativeDate(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return 'Vandaag';
  if (days === 1) return 'Gisteren';
  if (days < 7)  return `${days} dagen geleden`;
  if (days < 14) return '1 week geleden';
  if (days < 30) return `${Math.floor(days / 7)} weken geleden`;
  return new Date(iso).toLocaleDateString('nl-BE', { day: 'numeric', month: 'long' });
}

// ── Bar chart ─────────────────────────────────────────────────────────────────

function RoundedBar(props: { x?: number; y?: number; width?: number; height?: number; payload?: { barColor?: string } }) {
  const { x = 0, y = 0, width = 0, height = 0, payload } = props;
  if (height <= 0) return <g />;
  const fill = payload?.barColor ?? '#ec6a4c';
  const r = Math.min(4, width / 2);
  return <path d={`M${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} L${x},${y + height} Z`} fill={fill} />;
}

function BarTooltip({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  const n = payload[0].value;
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipLabel}>{label}</div>
      <div className={styles.tooltipValue}>{n} consult{n !== 1 ? 'en' : ''}</div>
    </div>
  );
}

function BarChart({ monthly, period }: { monthly: DoctorStatsResponse['monthly']; period: Period }) {
  if (monthly.length === 0) return <p className={styles.emptyChart}>Geen data voor deze periode.</p>;

  const labelEvery = Math.ceil(monthly.length / 8);
  const chartData  = monthly.map(m => ({ ...m, barColor: isCurrentBucket(m.month, period) ? 'rgba(236,106,76,0.28)' : '#ec6a4c' }));

  return (
    <ResponsiveContainer width="100%" height={188}>
      <ReBarChart data={chartData} barCategoryGap="28%" margin={{ top: 18, right: 4, left: 4, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#efe0d8" strokeDasharray="4 4" />
        <XAxis dataKey="month" axisLine={false} tickLine={false} interval={0}
          tickFormatter={(v: string, i: number) => {
            if (i % labelEvery !== 0) return '';
            return bucketLabel(v, period) + (isCurrentBucket(v, period) ? ' *' : '');
          }}
          tick={{ fontSize: 11, fill: '#7f5c4e', fontFamily: 'Open Sans, sans-serif' }} />
        <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={44}
          tick={{ fontSize: 10, fill: '#adadad', fontFamily: 'Open Sans, sans-serif' }} />
        <Tooltip content={<BarTooltip />} cursor={{ fill: 'rgba(236,106,76,0.06)' }} />
        <Bar dataKey="count" shape={<RoundedBar />} maxBarSize={48} />
      </ReBarChart>
    </ResponsiveContainer>
  );
}

// ── Income chart ──────────────────────────────────────────────────────────────

const INCOME_SERIES = [
  { key: 'big',   label: 'Nieuw consult',     color: '#3aad7a' },
  { key: 'small', label: 'Enkel voorschrift', color: '#3faecc' },
] as const;

type IncomePoint = DoctorStatsResponse['monthly'][number] & { current: boolean };

function formatEuro(cents: number): string {
  return (cents / 100).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function IncomeBar(props: { x?: number; y?: number; width?: number; height?: number; fill?: string; payload?: IncomePoint }) {
  const { x = 0, y = 0, width = 0, height = 0, fill, payload } = props;
  if (height <= 0) return <g />;
  const r = Math.min(4, width / 2);
  return <path d={`M${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} L${x},${y + height} Z`} fill={fill} fillOpacity={payload?.current ? 0.28 : 1} />;
}

function IncomeTooltip({ active, payload, period }: { active?: boolean; payload?: { dataKey: string; payload: IncomePoint }[]; period: Period }) {
  if (!active || !payload?.length) return null;
  const { dataKey, payload: p } = payload[0];
  const series = INCOME_SERIES.find(sr => dataKey === `${sr.key}_income_cents`)!;
  const count = p[`${series.key}_count`];
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipLabel}>{bucketLabel(p.month, period)} · {series.label}</div>
      <div className={styles.tooltipValue}>{formatEuro(p[`${series.key}_income_cents`])}</div>
      <div className={styles.tooltipLabel}>{count} consult{count !== 1 ? 'en' : ''}</div>
    </div>
  );
}

function IncomeChart({ monthly, period }: { monthly: DoctorStatsResponse['monthly']; period: Period }) {
  if (monthly.length === 0) return <p className={styles.emptyChart}>Geen data voor deze periode.</p>;

  const labelEvery = Math.ceil(monthly.length / 8);
  const chartData: IncomePoint[] = monthly.map(m => ({ ...m, current: isCurrentBucket(m.month, period) }));
  // Amount labels collide once there are too many bars (30-day / 3-month views)
  const showLabels = monthly.length <= 12;

  return (
    <ResponsiveContainer width="100%" height={188}>
      <ReBarChart data={chartData} barCategoryGap="24%" barGap={3} margin={{ top: 18, right: 4, left: 4, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#efe0d8" strokeDasharray="4 4" />
        <XAxis dataKey="month" axisLine={false} tickLine={false} interval={0}
          tickFormatter={(v: string, i: number) => {
            if (i % labelEvery !== 0) return '';
            return bucketLabel(v, period) + (isCurrentBucket(v, period) ? ' *' : '');
          }}
          tick={{ fontSize: 11, fill: '#7f5c4e', fontFamily: 'Open Sans, sans-serif' }} />
        <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={44}
          tickFormatter={(v: number) => formatEuro(v)}
          tick={{ fontSize: 10, fill: '#adadad', fontFamily: 'Open Sans, sans-serif' }} />
        <Tooltip shared={false} content={<IncomeTooltip period={period} />} cursor={{ fill: 'rgba(58,173,122,0.06)' }} />
        {INCOME_SERIES.map(({ key, color }) => (
          <Bar key={key} dataKey={`${key}_income_cents`} fill={color} shape={<IncomeBar />} maxBarSize={32}>
            {showLabels && (
              <LabelList dataKey={`${key}_income_cents`} position="top"
                formatter={v => (typeof v === 'number' && v > 0 ? formatEuro(v) : '')}
                style={{ fontSize: 10, fill: '#7f5c4e', fontFamily: 'Open Sans, sans-serif', fontWeight: 600 }} />
            )}
          </Bar>
        ))}
      </ReBarChart>
    </ResponsiveContainer>
  );
}

function IncomeSplit({ monthly }: { monthly: DoctorStatsResponse['monthly'] }) {
  const slices = INCOME_SERIES.map(({ key, label, color }) => ({
    label,
    fill:  color,
    value: monthly.reduce((sum, m) => sum + m[`${key}_income_cents`], 0),
    count: monthly.reduce((sum, m) => sum + m[`${key}_count`], 0),
  }));
  const total = slices.reduce((sum, sl) => sum + sl.value, 0);
  if (total === 0) return <p className={styles.emptyChart}>Geen data voor deze periode.</p>;

  return (
    <>
      <PieChart width={120} height={120} style={{ margin: '0 auto 18px' }}>
        <Pie data={slices} dataKey="value" innerRadius={36} outerRadius={56}
          startAngle={90} endAngle={-270} strokeWidth={0}>
          <Label content={({ viewBox }) => {
            const { cx = 60, cy = 60 } = (viewBox ?? {}) as { cx?: number; cy?: number };
            return (
              <g>
                <text x={cx} y={cy - 5} textAnchor="middle" fill="#2b1710"
                  fontSize={17} fontWeight={800} fontFamily="Open Sans, sans-serif">{formatEuro(total)}</text>
                <text x={cx} y={cy + 11} textAnchor="middle" fill="#7f5c4e"
                  fontSize={7.5} fontWeight={700} letterSpacing="0.06em"
                  fontFamily="Open Sans, sans-serif">INKOMSTEN</text>
              </g>
            );
          }} position="center" />
        </Pie>
      </PieChart>
      <div className={styles.donutLegend}>
        {slices.map(({ label, value, count, fill }) => (
          <div key={label} className={styles.legendRow}>
            <span className={styles.legendDot} style={{ background: fill }} />
            <span className={styles.legendName}>{label} <span className={styles.legendCount}>({count})</span></span>
            <span className={styles.legendVal}>{formatEuro(value)}</span>
            <span className={styles.legendPct}>{Math.round(value / total * 100)}%</span>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Donut chart ───────────────────────────────────────────────────────────────

function DonutChart({ data }: { data: DoctorStatsResponse }) {
  const total = data.gesloten + data.actief + data.wachtend;
  if (total === 0) return <p className={styles.emptyChart}>Geen data voor deze periode.</p>;

  const slices = [
    { label: 'Afgerond', value: data.gesloten, fill: '#3aad7a' },
    { label: 'Opvolging', value: data.actief,  fill: '#3faecc' },
    { label: 'Wachtend', value: data.wachtend, fill: '#ec6a4c' },
  ];

  return (
    <>
      <PieChart width={120} height={120} style={{ margin: '0 auto 18px' }}>
        <Pie data={slices} dataKey="value" innerRadius={36} outerRadius={56}
          startAngle={90} endAngle={-270} strokeWidth={0}>
          <Label content={({ viewBox }) => {
            const { cx = 60, cy = 60 } = (viewBox ?? {}) as { cx?: number; cy?: number };
            return (
              <g>
                <text x={cx} y={cy - 5} textAnchor="middle" fill="#2b1710"
                  fontSize={19} fontWeight={800} fontFamily="Open Sans, sans-serif">{total}</text>
                <text x={cx} y={cy + 11} textAnchor="middle" fill="#7f5c4e"
                  fontSize={7.5} fontWeight={700} letterSpacing="0.06em"
                  fontFamily="Open Sans, sans-serif">CONSULTEN</text>
              </g>
            );
          }} position="center" />
        </Pie>
      </PieChart>
      <div className={styles.donutLegend}>
        {slices.map(({ label, value, fill }) => (
          <div key={label} className={styles.legendRow}>
            <span className={styles.legendDot} style={{ background: fill }} />
            <span className={styles.legendName}>{label}</span>
            <span className={styles.legendVal}>{value}</span>
            <span className={styles.legendPct}>{Math.round(value / total * 100)}%</span>
          </div>
        ))}
      </div>
    </>
  );
}

// ── KPI grid ──────────────────────────────────────────────────────────────────

function KpiGrid({ data }: { data: DoctorStatsResponse }) {
  const earned = (data.earned_cents / 100).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 });

  return (
    <div className={styles.kpiGrid}>
      <div className={styles.kpiCard}>
        <div className={styles.kpiLabel}>Totale consulten</div>
        <div className={styles.kpiValue}>{data.total}</div>
      </div>
      <div className={styles.kpiCard}>
        <div className={styles.kpiLabel}>Gem. doorlooptijd</div>
        <div className={styles.kpiValue}>{data.avg_days !== null ? `${data.avg_days} d` : '–'}</div>
        <div className={styles.kpiNote}>SLA: 5 werkdagen</div>
      </div>
      <div className={styles.kpiCard}>
        <div className={styles.kpiLabel}>Gem. beoordeling</div>
        <div className={styles.kpiValue}>{data.avg_rating !== null ? data.avg_rating.toFixed(1) : '–'}</div>
        {data.avg_rating !== null && <div className={styles.kpiStars}>{starsDisplay(data.avg_rating)}</div>}
      </div>
      <div className={styles.kpiCard}>
        <div className={styles.kpiLabel}>Omzet</div>
        <div className={styles.kpiValue}>{earned}</div>
        <div className={styles.kpiNote}>betaald door patiënten, excl. terugbetalingen</div>
      </div>
    </div>
  );
}

function KpiSkeleton() {
  return (
    <div className={styles.kpiGrid}>
      {[...Array(4)].map((_, i) => <div key={i} className={`${styles.kpiCard} ${styles.skeleton}`} />)}
    </div>
  );
}

// ── Feedback section ──────────────────────────────────────────────────────────

function FeedbackSection({ data }: { data: DoctorStatsResponse }) {
  const [selectedStars, setSelectedStars] = useState<number | null>(null);
  const totalRated = data.rating_distribution.reduce((s, b) => s + b.count, 0);
  const bins: Record<number, number> = {};
  data.rating_distribution.forEach(b => { bins[b.stars] = b.count; });

  const visibleFeedback = selectedStars !== null
    ? data.recent_feedback.filter(f => f.star_rating === selectedStars)
    : [...data.recent_feedback].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 5);

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <span className={styles.cardKicker}>PATIËNTFEEDBACK</span>
        <span className={styles.cardNote}>{totalRated} beoordelingen</span>
      </div>
      <div className={styles.feedbackGrid}>
        <div>
          <div className={styles.ratingSummary}>
            <span className={styles.ratingBig}>
              {data.avg_rating !== null ? data.avg_rating.toFixed(1) : '–'}
            </span>
            <div>
              <div className={styles.ratingStars}>
                {data.avg_rating !== null ? starsDisplay(data.avg_rating) : '–––––'}
              </div>
              <div className={styles.ratingSub}>op basis van {totalRated} beoordelingen</div>
            </div>
          </div>
          <div className={styles.ratingBars}>
            {[5, 4, 3, 2, 1].map(stars => (
              <button key={stars}
                className={`${styles.rbarRow}${selectedStars === stars ? ` ${styles.rbarRowActive}` : ''}`}
                onClick={() => setSelectedStars(selectedStars === stars ? null : stars)}>
                <span className={styles.rbarLabel}>{stars}</span>
                <span className={styles.rbarStar}>★</span>
                <div className={styles.rbarBg}>
                  <div className={styles.rbarFill}
                    style={{ width: `${((bins[stars] ?? 0) / Math.max(totalRated, 1)) * 100}%` }} />
                </div>
                <span className={styles.rbarCount}>{bins[stars] ?? 0}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.quotesCol}>
          {visibleFeedback.length === 0
            ? <p className="text-muted">Nog geen feedback ontvangen.</p>
            : visibleFeedback.map((f, i) => (
              <div key={i} className={styles.quoteCard}>
                <div className={styles.quoteMeta}>
                  <span className={styles.quoteStars}>{'★'.repeat(f.star_rating)}{'☆'.repeat(5 - f.star_rating)}</span>
                  <span className={styles.quoteDate}>{formatRelativeDate(f.created_at)}</span>
                </div>
                <p className={styles.quoteText}>{f.feedback_text}</p>
                <p className={styles.quoteAuthor}>— {f.patient_name}</p>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function Statistics() {
  const authFetch = useAuthFetch();
  const [period, setPeriod] = useState<Period>('6m');

  const { data, isPending, isError } = useQuery({
    queryKey: ['doctor-statistics', period],
    queryFn: async () => {
      const res = await authFetch(`/doctor/statistics?period=${period}`);
      if (!res.ok) throw new Error();
      return res.json() as Promise<DoctorStatsResponse>;
    },
  });

  return (
    <div className={styles.container}>
    <div className={styles.inner}>
      <div className={styles.pageHead}>
        <div className={styles.titleGroup}>
          <span className={styles.kicker}>DASHBOARD</span>
          <h2 className={styles.heading}>Statistieken</h2>
        </div>
        <div className={styles.periodToggle}>
          {PERIODS.map(({ key, label }) => (
            <button key={key}
              className={`${styles.periodBtn}${period === key ? ` ${styles.periodBtnActive}` : ''}`}
              onClick={() => setPeriod(key)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {isError && <p className="text-muted status-message">Statistieken konden niet worden geladen.</p>}

      {isPending ? <KpiSkeleton /> : data && <KpiGrid data={data} />}

      {data && (
        <div className={styles.chartsRow}>
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <span className={styles.cardKicker}>{period === '30d' ? 'CONSULTEN PER DAG' : period === '3m' ? 'CONSULTEN PER WEEK' : 'CONSULTEN PER MAAND'}</span>
              <span className={styles.cardNote}>{period === '30d' ? '* lopende dag' : period === '3m' ? '* lopende week' : '* lopende maand'}</span>
            </div>
            <BarChart monthly={data.monthly} period={period} />
          </div>
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <span className={styles.cardKicker}>STATUS VERDELING</span>
            </div>
            <DonutChart data={data} />
          </div>
        </div>
      )}

      {data && (
        <div className={styles.chartsRow}>
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <span className={styles.cardKicker}>{period === '30d' ? 'INKOMSTEN PER DAG' : period === '3m' ? 'INKOMSTEN PER WEEK' : 'INKOMSTEN PER MAAND'}</span>
              <span className={styles.cardNote}>{period === '30d' ? '* lopende dag' : period === '3m' ? '* lopende week' : '* lopende maand'}</span>
            </div>
            <IncomeChart monthly={data.monthly} period={period} />
          </div>
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <span className={styles.cardKicker}>INKOMSTEN VERDELING</span>
            </div>
            <IncomeSplit monthly={data.monthly} />
          </div>
        </div>
      )}

      {data && <FeedbackSection data={data} />}
    </div>
    </div>
  );
}
